import { useEffect, useReducer, useRef, useState } from 'react';
import { BeakerIcon, HeartIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { userAppContext } from '../../../../context/Context';
import { patchItems } from '@/components/Patches/PatchSearchItems';
import { LinksExamples } from './LinksExamples';
import SearchButton from '../../../../global/SearchButton/SearchButton';
import type { Section as SectionType } from '../../../../types';
import SectionItem from './SectionItem';
import { initialState, searchComponentReducer } from '../../reducers/searchComponentReducer';
import { fetchData } from '../apiCalls';
import {
  ALL_RESULTS_TAB,
  buildNavigableEntries,
  getDropdownTabs,
  getSelectionTagLabel,
  getVisibleSections,
  handleSearchItems,
  rankSectionsByRelevance,
  resolveActiveTab,
  tagMapping,
} from '../helpers';
import { DATASETS } from '@/components/Constants';

const CategoryIcon = ({ category }: { category: string }) => {
  const className = 'search-dropdown-cat-icon';
  if (category === DATASETS.DISEASES) return <HeartIcon className={className} aria-hidden />;
  if (category === DATASETS.GENES) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M7 4c4 4 6 12 10 16M17 4c-4 4-6 12-10 16M8.5 9h7M8.5 15h7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (category === DATASETS.VARIANTS) return <SparklesIcon className={className} aria-hidden />;
  if (category === DATASETS.CHEMICALS) return <BeakerIcon className={className} aria-hidden />;
  return null;
};

const Dropdown = ({ value, setValue, searchItems, setSearchItems, onDropdownToggle }) => {
  const [data, setData] = useState<any>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(ALL_RESULTS_TAB);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const cancelTokenRef = useRef<any>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigableEntriesRef = useRef<ReturnType<typeof buildNavigableEntries>>([]);
  const focusedIndexRef = useRef(0);
  const showResultsRef = useRef(false);
  const searchItemsRef = useRef(searchItems);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  const [state, dispatch] = useReducer(searchComponentReducer, initialState);
  const context = userAppContext();
  const authorization = context?.authorization;
  const isAuthenticated = context?.isAuthenticated;

  const noData =
    !data ||
    (data.diseases?.length === 0 &&
      data.genes?.length === 0 &&
      data.variants?.length === 0 &&
      data.chemicals?.length === 0);

  const sections: SectionType[] = noData
    ? []
    : rankSectionsByRelevance(handleSearchItems(searchItems, data), value);
  const resolvedTab = resolveActiveTab(sections, activeTab);
  const tabs = getDropdownTabs(sections);
  const visibleSections = getVisibleSections(sections, resolvedTab);
  const navigableEntries = buildNavigableEntries(visibleSections);
  const showTabs = tabs.length > 1;
  const showResults = !state.isLoading && !error && !noData;
  const selectedCount = searchItems.length;

  navigableEntriesRef.current = navigableEntries;
  focusedIndexRef.current = focusedIndex;
  showResultsRef.current = showResults;
  searchItemsRef.current = searchItems;

  const handleSearch = async (pattern: string) => {
    setError('');
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const payload = await fetchData({
        cancelToken: cancelTokenRef,
        authorization,
        isAuthenticated,
        value: pattern,
      });
      setData(payload);
    } catch (err: any) {
      setError(err.message);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const removeSelectedItem = (itemId: string) => {
    setSearchItems(prev => prev.filter(item => item.id !== itemId));
  };

  useEffect(() => {
    if (value.trim().length < 3) {
      setData(undefined);
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    const timeoutId = setTimeout(() => {
      handleSearch(value);
    }, 300);
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Cleanup');
    }
    return () => clearTimeout(timeoutId);
  }, [value, authorization, isAuthenticated]);

  useEffect(() => {
    onDropdownToggle(true);
    return () => onDropdownToggle(false);
  }, [value, onDropdownToggle]);

  useEffect(() => {
    setActiveTab(ALL_RESULTS_TAB);
    setFocusedIndex(0);
  }, [value]);

  useEffect(() => {
    setActiveTab(resolvedTab);
    setFocusedIndex(0);
  }, [resolvedTab]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setValue('');
        return;
      }

      const entries = navigableEntriesRef.current;
      if (!showResultsRef.current || entries.length === 0) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        setFocusedIndex(current => (current + delta + entries.length) % entries.length);
        return;
      }

      if (event.key !== 'Enter') return;

      const entry = entries[focusedIndexRef.current];
      if (!entry) return;
      event.preventDefault();
      if (entry.type === 'seeAll') {
        setActiveTab(entry.sectionTitle);
        return;
      }

      const patched = patchItems(entry.item);
      const selectedDataset = searchItemsRef.current[0]?.dataset;
      if (selectedDataset && patched.dataset && selectedDataset !== patched.dataset) return;

      setSearchItems(prev =>
        prev.some(prevItem => prevItem.id === patched.id)
          ? prev.filter(prevItem => prevItem.id !== patched.id)
          : [...prev, patched],
      );
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [setSearchItems, setValue]);

  useEffect(() => {
    const updateMaxHeight = () => {
      const node = containerRef.current;
      if (!node) return;
      const top = node.getBoundingClientRect().top;
      const viewport = window.visualViewport?.height ?? window.innerHeight;
      const available = Math.floor(viewport - top - 16);
      setMaxHeight(Math.max(220, Math.min(available, 560)));
    };

    updateMaxHeight();
    window.addEventListener('resize', updateMaxHeight);
    window.addEventListener('scroll', updateMaxHeight, true);
    window.visualViewport?.addEventListener('resize', updateMaxHeight);
    window.visualViewport?.addEventListener('scroll', updateMaxHeight);

    return () => {
      window.removeEventListener('resize', updateMaxHeight);
      window.removeEventListener('scroll', updateMaxHeight, true);
      window.visualViewport?.removeEventListener('resize', updateMaxHeight);
      window.visualViewport?.removeEventListener('scroll', updateMaxHeight);
    };
  }, [value, showResults, selectedCount, resolvedTab, state.isLoading]);

  useEffect(() => {
    const focusedNode = listRef.current?.querySelector(`[data-nav-index="${focusedIndex}"]`);
    if (focusedNode instanceof HTMLElement) {
      focusedNode.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, resolvedTab]);

  let navCursor = 0;

  return (
    <div
      ref={containerRef}
      id="react-autowhatever-1"
      className="react-dropdown-container"
      data-testid="dropdown-div"
      role="presentation"
      style={maxHeight ? { maxHeight } : undefined}
    >
      {showTabs && (
        <div className="search-dropdown-tabs" role="tablist" aria-label="Result categories">
          {tabs.map(tab => {
            const selected = tab.id === resolvedTab;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                data-testid={`search-dropdown-tab-${tab.id}`}
                data-category={tab.category}
                className={`search-dropdown-tab${selected ? ' is-active' : ''}`}
                onMouseDown={event => event.preventDefault()}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.showDot ? (
                  <span className={`search-dropdown-tab__dot${selected ? ' is-active' : ''}`} aria-hidden />
                ) : null}
                <span>
                  {tab.label}{' '}
                  <span className={`search-dropdown-section__total-count${selected ? ' is-active' : ''}`}>
                    {tab.count}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div ref={listRef} className="search-dropdown-body" role="listbox" aria-label="Search suggestions">
        {state.isLoading && noData ? (
          <span className="search-dropdown-status">Loading...</span>
        ) : error ? (
          <span className="search-dropdown-status">
            {error.includes('403') ? 'Session expired. Please refresh page' : `Error: ${error}`}
          </span>
        ) : noData ? (
          <span className="search-dropdown-status">No results found</span>
        ) : (
          visibleSections.map(section => {
            const items = section.preview.map((item, index) => {
              const navIndex = navCursor;
              navCursor += 1;
              const patched = patchItems(item);
              return (
                <SectionItem
                  key={patched.id ?? `${section.title}-${index}`}
                  item={item}
                  query={value}
                  setValue={setValue}
                  isFocused={focusedIndex === navIndex}
                  optionId={`search-dropdown-option-${navIndex}`}
                  navIndex={navIndex}
                />
              );
            });
            const seeAllIndex = section.truncated ? navCursor : null;
            if (section.truncated) navCursor += 1;

            return (
              <section
                key={section.title}
                className="search-dropdown-section"
                data-category={section.title}
                aria-labelledby={`search-dropdown-heading-${section.title}`}
              >
                <div className="search-dropdown-section__header">
                  <div className="search-dropdown-section__heading">
                    <CategoryIcon category={section.title} />
                    <h3 id={`search-dropdown-heading-${section.title}`}>
                      {section.title}{' '}
                      <span className="search-dropdown-section__total">
                        {resolvedTab === ALL_RESULTS_TAB
                          ? `${section.preview.length} of ${section.total}`
                          : section.total}
                      </span>
                    </h3>
                  </div>
                  {section.truncated ? (
                    <>
                      <span className="search-dropdown-section__divider" aria-hidden />
                      <button
                        type="button"
                        className={`search-dropdown-see-all${focusedIndex === seeAllIndex ? ' is-focused' : ''}`}
                        data-testid={`search-dropdown-see-all-${section.title}`}
                        data-nav-index={seeAllIndex ?? undefined}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => setActiveTab(section.title)}
                      >
                        See all {section.total}
                      </button>
                    </>
                  ) : null}
                </div>
                <ul className="react-dropdown-list">{items}</ul>
              </section>
            );
          })
        )}
      </div>

      <div className="search-dropdown-footer">
        <div className="search-dropdown-footer__bar">
          {selectedCount > 0 ? (
            <>
              <div className="search-dropdown-selection__tags" aria-label="Selected items">
                {searchItems.map((item, index) => {
                  const patched = patchItems(item);
                  const label = getSelectionTagLabel(patched);
                  return (
                    <button
                      key={patched.id ?? index}
                      type="button"
                      className="search-dropdown-chip"
                      data-category={patched.dataset}
                      data-testid="searchTag"
                      title={tagMapping(patched).tooltip}
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => removeSelectedItem(patched.id)}
                    >
                      <span className="search-dropdown-chip__dot" aria-hidden />
                      <span className="search-dropdown-chip__label">{label}</span>
                      <XMarkIcon className="search-dropdown-chip__remove" aria-hidden />
                      <span className="sr-only">Remove {label}</span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                id="clearAll"
                className="search-dropdown-clear-all"
                onMouseDown={event => event.preventDefault()}
                onClick={() => setSearchItems([])}
              >
                Clear all
              </button>
            </>
          ) : showResults ? null : (
            <LinksExamples searchItems={searchItems} setSearchItems={setSearchItems} />
          )}
          <SearchButton
            setValue={setValue}
            id="search-btn-text"
            size="sm"
            variant="primary"
            className="search-dropdown-search-btn"
          >
            {selectedCount > 0 ? `Search ${selectedCount} selected` : 'Search'}
          </SearchButton>
        </div>
      </div>
    </div>
  );
};

export default Dropdown;
